import { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { UIHooks } from '@karnott/hooks';
import GoToMobileApp from '../components/GoToMobileApp';
import Header from '../components/Header';
import HelpDemoModal from '../components/HelpDemoModal';
import LeaveIE from '../components/LeaveIE';
import SnackBar from '../components/SnackBar';
import { PluginsContext } from '../contexts/PluginsProvider';
import { UIContext } from '../contexts/UIProvider';
import Routes from '../routes';
import { fetchAllArticles } from '../slices/articles';
import { fetchBeacons } from '../slices/beacons';
import { fetchClusters } from '../slices/clusters';
import { fetchCrops } from '../slices/crops';
import { fetchDevices } from '../slices/devices';
import { fetchEquipmentModels, fetchEquipments } from '../slices/equipments';
import { fetchObservationCategories, fetchObservations } from '../slices/observations/general';
import { fetchParcels } from '../slices/parcels';
import { fetchAllMixtures } from '../slices/phyto';
import { fetchTags } from '../slices/tags';
import { fetchAllTaskTypes } from '../slices/taskTypes';
import { selectTitle, setInitialTags } from '../slices/ui';
import { fetchUsers, retrieveMe, selectCurrentUser, userLoggedOut } from '../slices/users';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { color } from '../ui/theme';
import { isMobile } from '../utils';
import { startCobrowse } from '../utils/Cobrowse';

const Wrapper = styled.div`
  height: 100%;
  background-color: ${color('snow', 'light')};
  display: flex;
  flex-direction: column;
`;

const AppContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

function AppContainer() {
  const { hasOverallCropManagementPlugin, hasOverallObservationPlugin } = useContext(PluginsContext);
  const { demoAccount } = useContext(UIContext);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();

  const user = useAppSelector(selectCurrentUser);
  const title = useAppSelector(selectTitle);

  const leaveIE = Boolean(
    navigator.appName === 'Microsoft Internet Explorer' ||
    navigator.userAgent.match(/Trident/) ||
    navigator.userAgent.match(/rv:11/) ||
    navigator.userAgent.match(/MSIE/g),
  );
  const mobile = isMobile();
  const [isMobileOpen, , closeMobile] = UIHooks.useOpenCloseState(mobile);

  const redirectToOAuth = useCallback(() => {
    localStorage.setItem('fromURL', location.pathname || '/');
    history.replace('/logout');
  }, [location, history]);

  const hasValidToken = useMemo(() => {
    if (!user?.isImpersonating) {
      return true;
    }
    if (user?.expireDate) {
      const now = new Date();
      const almostExpired = new Date(user.expireDate);
      almostExpired.setSeconds(almostExpired.getSeconds() - 10);
      return now <= almostExpired;
    }
    return true;
  }, [user]);

  const loginSafety = useCallback(() => {
    if (!hasValidToken) {
      redirectToOAuth();
    }
  }, [hasValidToken, redirectToOAuth]);

  useEffect(() => {
    loginSafety();
  }, [loginSafety]);

  const withToken = useCallback(
    (call) => {
      if (hasValidToken) {
        call();
      }
    },
    [hasValidToken],
  );

  useEffect(() => {
    withToken(() => {
      dispatch(fetchParcels());
      dispatch(fetchEquipmentModels());
      dispatch(fetchEquipments());
      dispatch(fetchDevices());
      dispatch(fetchBeacons());
      dispatch(fetchUsers());
      dispatch(fetchTags());
      dispatch(fetchAllMixtures());
      dispatch(fetchAllTaskTypes());
      dispatch(fetchAllArticles());
    });
  }, [dispatch, withToken]);

  useEffect(() => {
    if (hasOverallCropManagementPlugin) {
      withToken(() => dispatch(fetchCrops()));
    }
  }, [dispatch, hasOverallCropManagementPlugin, withToken]);

  useEffect(() => {
    if (hasOverallObservationPlugin) {
      withToken(() => dispatch(fetchObservations('?status=AVAILABLE')));
      withToken(() => dispatch(fetchObservationCategories()));
    }
  }, [dispatch, hasOverallObservationPlugin, withToken]);

  useEffect(() => {
    if (hasValidToken) {
      dispatch(retrieveMe()).then(({ payload: retrievedUser }) => {
        if (retrievedUser?.application_role !== 'USER') {
          alert(`pro.karnott.fr n'est accessible qu'aux comptes utilisateurs`);
          dispatch(userLoggedOut());
        } else if (retrievedUser) {
          void startCobrowse(retrievedUser);

          if (retrievedUser.tag_ids_preference) {
            dispatch(setInitialTags(retrievedUser.tag_ids_preference?.parcel || []));
          }
        }
      });
    }
  }, [dispatch, hasValidToken]);

  useEffect(() => {
    withToken(() => {
      dispatch(fetchClusters()).then(({ payload: clusters }) => {
        const hasUnpaid = clusters.some((cluster) => cluster.current_state === 'UNPAID');
        if (hasUnpaid) {
          history.replace('/unpaid');
        }
      });
    });
  }, [dispatch, withToken, history]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  if (!hasValidToken) {
    return null;
  } else {
    return (
      <Wrapper id="app-element">
        <Header />
        <AppContent id="app-content">
          <Routes />
        </AppContent>
        <GoToMobileApp isOpen={isMobileOpen} onRequestClose={closeMobile} />
        {demoAccount ? <HelpDemoModal /> : null}
        <LeaveIE isOpen={leaveIE} />
        <SnackBar />
      </Wrapper>
    );
  }
}

export default memo(AppContainer);
